import { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import {
  getEffectiveCourseDiscount,
  calculateDiscountedPrice,
} from "../../utils/skiworkUtils";
import { getBestFittingSessionIndex } from "../../utils/privateOffersUtils";

/** Numero massimo di persone acquistabili online per un pacchetto. */
const MAX_PERSONE = 5;

/**
 * Stato e derivazioni della prenotazione di una "Offerta / Pacchetto" (corso con flag
 * CorsoPrivato), estratti da `DettaglioOfferta.js` per essere riusati anche dalla variante
 * a modale del flusso private_new, senza duplicare le regole di prezzo/sconto.
 *
 * `selectedDates` e `preferredNumAllievi` sono opzionali e completamente inerti se assenti:
 * quando non passati il comportamento è bit-a-bit identico a quello storico di DettaglioOfferta.
 */
export default function useOffertaBooking({
  corso,
  sessioniCorso,
  datiScuola,
  promozioniNew,
  appData,
  selectedDates,
  preferredNumAllievi,
}) {
  const [idxSessione, setIdxSessione] = useState(0);
  const [numAllievi, setNumAllievi] = useState(1);
  const [coordinateRitrovo, setCoordinateRitrovo] = useState(undefined);
  const [mapVisible, setMapVisible] = useState(false);

  const sessioni = sessioniCorso?.sessioni ?? [];
  const sessioneSelezionata = sessioni[idxSessione];

  // Il pacchetto si acquista per intero: tutte le giornate della sessione.
  const giorniOfferta =
    sessioneSelezionata?.listaDateEffettive?.length ??
    sessioneSelezionata?.numGiorni ??
    0;
  const numSessioniEffettive = sessioneSelezionata?.righe?.length ?? 0;

  /**
   * Persone acquistabili: solo gli slot che la scuola ha effettivamente valorizzato.
   * Senza il filtro sul prezzo, una scuola che ha compilato solo Prz1gg..Prz3gg lascerebbe
   * comprare il pacchetto per 5 persone a 0.
   */
  const numAllieviDisponibili = useMemo(() => {
    const listino = sessioneSelezionata?.listino ?? [];
    return listino
      .filter((x) => x.numGiorni >= 1 && x.numGiorni <= MAX_PERSONE && x.prezzo > 0)
      .sort((a, b) => a.numGiorni - b.numGiorni)
      .map((x) => ({
        key: x.numGiorni,
        text: String(x.numGiorni),
        value: x.numGiorni,
      }));
  }, [sessioneSelezionata]);

  // Preselezione della sessione migliore in base alle date già scelte dal cliente nel pannello
  // sopra (modale private_new). Le sessioni arrivano in modo asincrono, quindi va rieseguito
  // finché non sono disponibili; una volta fatto una volta il dropdown "Periodo" appartiene
  // all'utente e rieseguirlo gli annullerebbe la scelta (stessa logica di OfferCourseDialog.tsx,
  // hasPreselectedSessionRef).
  const hasPreselectedSessionRef = useRef(false);
  useEffect(() => {
    hasPreselectedSessionRef.current = false;
  }, [corso?.idCorso]);

  useEffect(() => {
    if (!selectedDates || selectedDates.length === 0) return;
    if (hasPreselectedSessionRef.current) return;
    if (sessioni.length === 0) return;

    hasPreselectedSessionRef.current = true;
    setIdxSessione(getBestFittingSessionIndex(sessioni, selectedDates));
  }, [selectedDates, sessioni]);

  // Tengo il numero di persone dentro a quanto il periodo scelto è realmente prezzato.
  // Se il chiamante (modale private_new) ha già una preferenza sul numero di persone -
  // l'utente ha già risposto "quanti siete?" nel pannello sopra - la prima volta che il
  // listino del periodo scelto diventa disponibile la applico, se il periodo la prezza
  // davvero; riaprire il pacchetto su "1 allievo" contraddirebbe in silenzio quella risposta.
  // La preselezione della sessione migliore (sopra) cambia idxSessione DOPO che questo effetto
  // ha già consumato il one-shot: il listino cambia di nuovo e, se il valore corrente non è più
  // prezzato, riconsidero comunque preferredNumAllievi (se presente nel nuovo listino) prima di
  // ripiegare sul primo disponibile, così non tradisco la risposta dell'utente solo perché il
  // periodo è cambiato sotto di lei (stessa logica di OfferCourseDialog.tsx, setSelectedPupils).
  const hasAppliedPreferredNumAllieviRef = useRef(false);
  useEffect(() => {
    hasAppliedPreferredNumAllieviRef.current = false;
  }, [corso?.idCorso]);

  useEffect(() => {
    if (numAllieviDisponibili.length === 0) return;

    if (
      typeof preferredNumAllievi === "number" &&
      !hasAppliedPreferredNumAllieviRef.current
    ) {
      hasAppliedPreferredNumAllieviRef.current = true;
      if (numAllieviDisponibili.some((x) => x.value === preferredNumAllievi)) {
        setNumAllievi(preferredNumAllievi);
        return;
      }
    }

    if (!numAllieviDisponibili.some((x) => x.value === numAllievi)) {
      if (
        typeof preferredNumAllievi === "number" &&
        numAllieviDisponibili.some((x) => x.value === preferredNumAllievi)
      ) {
        setNumAllievi(preferredNumAllievi);
      } else {
        setNumAllievi(numAllieviDisponibili[0].value);
      }
    }
  }, [numAllieviDisponibili]);

  // Il ritrovo cambia col periodo, quindi le coordinate vanno ricalcolate a ogni cambio sessione.
  useEffect(() => {
    setMapVisible(false);
    setCoordinateRitrovo(undefined);

    const coordinate = sessioneSelezionata?.coordinateRitrovo;
    if (!coordinate || coordinate.length < 20) return;

    const values = coordinate.split(",");
    if (values.length !== 3) return;

    setCoordinateRitrovo({
      lat: parseFloat(values[0]),
      lng: parseFloat(values[1]),
      z: parseFloat(values[2]),
    });
  }, [idxSessione, sessioneSelezionata]);

  const hasAgency = Boolean(appData?.agenzia);
  const skipPromo = hasAgency && !appData.agenzia.applicaPromozioni;
  // Il pacchetto ha una sola giornata di riferimento: l'inizio della sessione scelta ("dal").
  const firstLessonDate = sessioneSelezionata?.dal;
  const promoPerc = skipPromo
    ? 0
    : getEffectiveCourseDiscount(corso, datiScuola, promozioniNew, firstLessonDate);
  const agencyPerc = hasAgency ? appData?.agenzia?.sconto ?? 0 : 0;

  /** Voce di listino per il numero di persone scelto, con promo e sconto agenzia applicati. */
  function listino() {
    const item = (sessioneSelezionata?.listino ?? []).find(
      (x) => parseInt(numAllievi, 10) === x.numGiorni
    );
    if (!item) return item;

    const roundingMode = promozioniNew?.discountRoundingMode ?? datiScuola.arrotondamentoVIP ?? 0;
    const discountedPrezzo = calculateDiscountedPrice(
      item.prezzo,
      promoPerc,
      agencyPerc,
      roundingMode
    );

    let discountedPrezzoPag = item.prezzoPag;
    if (item.prezzo === item.prezzoPag) {
      discountedPrezzoPag = discountedPrezzo;
    }

    return {
      ...item,
      prezzo: discountedPrezzo,
      prezzoPag: discountedPrezzoPag,
      prezzoListinoRiferimento: item.prezzo,
    };
  }

  const listinoInUso = listino();
  const periodoOptions = sessioni.map((sessione, index) => ({
    key: index,
    value: index,
    text: `${moment(sessione.dal).format("DD/MM/YYYY")} → ${moment(
      sessione.al
    ).format("DD/MM/YYYY")}`,
  }));

  /**
   * Argomenti per `props.onAvantiClick(...)`, stesso ordine di sempre. Ritorna `null` quando
   * manca sessione o listino (nulla da vendere), così i chiamanti possono usare lo spread solo
   * quando c'è davvero un prezzo valido.
   */
  function buildAvantiArgs() {
    if (!sessioneSelezionata || !listinoInUso) return null;

    return [
      moment(sessioneSelezionata.dal).format("YYYY-MM-DD"),
      giorniOfferta,
      numSessioniEffettive,
      numAllievi,
      listinoInUso.prezzo,
      listinoInUso.prezzoPag,
      listinoInUso.prezzoListino,
      undefined,
      promoPerc,
      agencyPerc,
      listinoInUso.prezzoListinoRiferimento,
    ];
  }

  return {
    sessioni,
    idxSessione,
    setIdxSessione,
    sessioneSelezionata,
    giorniOfferta,
    numSessioniEffettive,
    numAllievi,
    setNumAllievi,
    numAllieviDisponibili,
    periodoOptions,
    listinoInUso,
    promoPerc,
    agencyPerc,
    coordinateRitrovo,
    mapVisible,
    setMapVisible,
    buildAvantiArgs,
  };
}
